---
pageId: concept-insights
title: Statistics and insights
description: How Samlla turns your entries into understanding — Stats views, per-tracker baselines and trends.
category: concepts
order: 4
lang: en
slug: concepts/insights
updated: 2026-11-01
---

Samlla's statistics observe; they don't judge. There are no targets to miss and no warning colors — just your own data, compared with your own history, described in neutral terms. Insights live on four surfaces.

## The Stats page

**Stats** shows all trackers together across four granularities — **Week**, **Month**, **Quarter** and **Year** — each in two period modes: **Calendar** (aligned weeks and months) or **Rolling** (the last 7 days, last 6 weeks, last 90 days).

Every view combines:

- **A summary strip** — activities and training time, with a signed percent change once the period has closed.
- **An activity heatmap** — hours across the week in the weekly view, days across the period in the wider ones. Density shows as opacity; hovering reveals the exact counts.
- **Tracker cards** — the primary value, a per-day mini chart, and in the weekly view two quiet comparisons: against the previous week and against the median of your active weeks.
- **Quarter trends** — how each tracker moved across the quarter, sorted by the size of the change.
- **Records from the viewed period** — one section combines the best single entry with a still-standing record week, month or year. Every row names the record type and the delta reference; the complete history remains on the tracker page. Displaced period records are not reconstructed in older views. Everything is stated as fact, without fanfare.

You can filter everything by category (recursively), tracker or tags.

## Where I am — the tracker's default tab

Each tracker opens on a question everyone actually asks: what is typical for me? The **Where I am** tab builds a baseline from your history and answers in words: "usually 21 km", "Around your typical level", "More than your typical level", "Trending up". A trajectory line adds a calm projection — at this pace, where the month ends. When the history is too short, Samlla says so plainly and shows plain numbers instead of pretending to know.

## Insights — the tracker's deep dive

The **Insights** tab looks at one tracker over 30, 90, 365 days or all time: trends over time, distributions ("Tuesday is your day", where your values usually land), correlations between the tracker's own metrics, and a 365-day activity heatmap with an active-days counter.

## Free and Pro

Free includes every Stats view, including Year, with 90 days of visible history. Earlier days stay in the yearly layout as masked fields. Pro unlocks the complete history and removes plan limits on trackers, metrics and categories.
